import { useState } from "react";
import { makeStyles } from "@fluentui/react-components";
import { ThumbsUp, ThumbsDown } from "@phosphor-icons/react";
import UserAdditionalFeedback from "./UserAdditionalFeedback";
import { tokens } from "../../theme";

// =============================================================================
// TOKEN SHORTCUTS
// =============================================================================

const t = {
    // Spacing
    spacingVerticalXs: tokens.primitives["spacing-Vertical-XS"],
    spacingVerticalS: tokens.primitives["spacing-Vertical-S"],
    spacingHorizontalS: tokens.primitives["spacing-Horizontal-S"],
    spacingHorizontalM: tokens.primitives["spacing-Horizontal-M"],

    // Typography
    fontFamilyBase: tokens.primitives["font-Family-Base"],
    fontSizeBase300: tokens.primitives["font-Size-Base-300"],
    fontWeightRegular: tokens.primitives["font-Weight-Regular"],

    // Colors
    neutralSecondary: tokens.semantic["neutral-Secondary"],
    neutralLighterAlt: tokens.semantic["neutral-LighterAlt"],
    brandSecondaryFg: tokens.semantic["brandSecondaryForeground"],

    // Accent / Success
    accentSecondaryFg: tokens.semantic["brandAccentSecondaryForeground"],
    statusSuccessStrokePrimary: tokens.semantic["statusSuccessStrokePrimary"],
    statusSuccessBgPrimaryHover: tokens.semantic["statusSuccessBackgroundPrimaryHover"],

    // Radius
    radiusSmall: tokens.primitives["stroke-Radius-Small"],
    radiusMedium: tokens.primitives["stroke-Radius-Medium"],

    // Stroke
    strokeFocus: tokens.primitives.strokeFocus
};

// =============================================================================
// STYLES
// =============================================================================

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: t.spacingVerticalS
    },

    iconContainer: {
        display: "flex",
        flexDirection: "row",
        gap: t.spacingHorizontalS
    },

    thumbButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "none",
        border: "none",
        padding: t.spacingVerticalXs,
        cursor: "pointer",
        color: t.neutralSecondary,
        borderRadius: t.radiusMedium,
        transitionProperty: "color, background-color",
        transitionDuration: "0.15s",

        "&:hover": {
            color: t.brandSecondaryFg,
            backgroundColor: t.neutralLighterAlt
        },

        "&:focus-visible": {
            outline: `2px solid ${t.strokeFocus}`,
            outlineOffset: "2px"
        }
    },

    thumbButtonSelected: {
        color: t.brandSecondaryFg,

        "&:hover": {
            color: t.brandSecondaryFg
        }
    },

    feedbackConfirmation: {
        display: "inline-flex",
        alignItems: "center",
        backgroundColor: t.statusSuccessBgPrimaryHover,
        border: `1px solid ${t.statusSuccessStrokePrimary}`,
        borderRadius: t.radiusSmall,
        padding: `${t.spacingVerticalXs} ${t.spacingHorizontalM}`,
        fontFamily: t.fontFamilyBase,
        fontSize: t.fontSizeBase300,
        fontWeight: t.fontWeightRegular as unknown as number,
        color: t.accentSecondaryFg
    }
});

// =============================================================================
// COMPONENT
// =============================================================================

export default function UserRating(props: {
    handleFeedback?: (feedback: number) => void;
    handleAdditionalFeedback?: (feedbackOptions: { option_id: number; option_text: string }[], userInput: string) => void;
}) {
    const styles = useStyles();
    const [liked, setLiked] = useState(false);
    const [disliked, setDisliked] = useState(false);
    const [hoveredLike, setHoveredLike] = useState(false);
    const [hoveredDislike, setHoveredDislike] = useState(false);

    const handleLike = () => {
        setDisliked(false);
        const newVal = !liked;
        setLiked(newVal);
        props.handleFeedback?.(newVal ? 1 : 0);
    };

    const handleDislike = () => {
        setLiked(false);
        const newVal = !disliked;
        setDisliked(newVal);
        props.handleFeedback?.(newVal ? -1 : 0);
    };

    const feedbackSubmitted = (feedbackOptions: { option_id: number; option_text: string }[], userInput: string) => {
        props.handleAdditionalFeedback?.(feedbackOptions, userInput);
    };

    return (
        <div className={styles.container} role="group" aria-label="Rate this response">
            <div className={styles.iconContainer}>
                <button
                    className={`${styles.thumbButton} ${liked ? styles.thumbButtonSelected : ""}`}
                    onClick={handleLike}
                    onMouseEnter={() => setHoveredLike(true)}
                    onMouseLeave={() => setHoveredLike(false)}
                    aria-label="Like this answer"
                    aria-pressed={liked}
                >
                    <ThumbsUp size={20} weight={liked || hoveredLike ? "fill" : "regular"} aria-hidden="true" />
                </button>
                <button
                    className={`${styles.thumbButton} ${disliked ? styles.thumbButtonSelected : ""}`}
                    onClick={handleDislike}
                    onMouseEnter={() => setHoveredDislike(true)}
                    onMouseLeave={() => setHoveredDislike(false)}
                    aria-label="Dislike this answer"
                    aria-pressed={disliked}
                >
                    <ThumbsDown size={20} weight={disliked || hoveredDislike ? "fill" : "regular"} aria-hidden="true" />
                </button>
            </div>

            {(liked || disliked) && (
                <span className={styles.feedbackConfirmation} role="status" aria-live="polite">
                    Thank you for your feedback.
                </span>
            )}

            {disliked && <UserAdditionalFeedback feedbackSubmitted={feedbackSubmitted} />}
        </div>
    );
}
